import { ITheme, memoizeFunction } from '@fluentui/react';

import { AppTheme, AppThemeManager, isSharedUXDarkModeOn, isThemingEnabled } from '@1js/acui-theme';

import { defaultAppColorPalette } from '../styling/appColorPalette';
import { AppColors, getModernUxExperimentPreference } from '../utilities';

export interface PrimaryControlSemanticColors {
  primaryDefaultBackgroundColor: string;
  primaryHoverBackgroundColor: string;
  primarySelectedBackgroundColor: string;
  primaryPressedBackgroundColor: string;
  primaryFocusBackgroundColor: string;
  primaryFocusBorderColor: string;
  primaryDisabledBackgroundColor: string;
  primaryDisabledBorderColor: string;
  primaryFocusDisabledBackgroundColor: string;
  defaultTextLabelColorForPrimaryBackground: string;
  textDisabledColorForPrimaryBackground: string;
  primaryTextColor: string;
  primaryTextHoverColor: string;
  primaryTextActiveColor: string;
  controlBackgroundColorForPrimaryBackground: string;
  controlHoverBackgroundColorForPrimaryBackground: string;
  controlPressedBackgroundColorForPrimaryBackground: string;
}

/**
 * Obtain Primary Control Semantic Color definitions using AppColors (BEING DEPRECATED)
 * This method is here temporarily during a transition between the old usage of AppColors and the new usage of Theme.
 * It is meant to ensure that the old appColors usage is not lost if this semantic definitions are used outside of the Theming flight.
 */
const primaryControlSemanticColors = memoizeFunction(
  (appColors?: AppColors): PrimaryControlSemanticColors => {
    if (appColors) {
      return {
        /**
         * Common control scenarios that use App specific primary colors and its shades/tints
         */
        primaryDefaultBackgroundColor: appColors.primary,
        primaryHoverBackgroundColor: appColors.shade10,
        primarySelectedBackgroundColor: appColors.shade20,
        primaryPressedBackgroundColor: appColors.shade30,
        primaryFocusBackgroundColor: appColors.shade10,
        primaryFocusBorderColor: defaultAppColorPalette.white,
        primaryDisabledBackgroundColor: defaultAppColorPalette.gray20,
        primaryDisabledBorderColor: defaultAppColorPalette.gray20,
        primaryFocusDisabledBackgroundColor: defaultAppColorPalette.gray20,
        defaultTextLabelColorForPrimaryBackground: defaultAppColorPalette.white,
        textDisabledColorForPrimaryBackground: defaultAppColorPalette.gray70,
        primaryTextColor: appColors.primary,
        primaryTextHoverColor: appColors.shade10,
        primaryTextActiveColor: appColors.shade20,
        controlBackgroundColorForPrimaryBackground: defaultAppColorPalette.white,
        controlHoverBackgroundColorForPrimaryBackground: appColors.shade10,
        controlPressedBackgroundColorForPrimaryBackground: appColors.shade30,
      };
    }
    // if the appColors are not provided, use default appColors (as the pre-existing implementation did; not recommended though)
    return {
      /**
       * Common control scenarios that use App specific primary colors and its shades/tints
       */
      primaryDefaultBackgroundColor: defaultAppColorPalette.primary,
      primaryHoverBackgroundColor: defaultAppColorPalette.shade10,
      primarySelectedBackgroundColor: defaultAppColorPalette.shade20,
      primaryPressedBackgroundColor: defaultAppColorPalette.shade30,
      primaryFocusBackgroundColor: defaultAppColorPalette.shade10,
      primaryFocusBorderColor: defaultAppColorPalette.white,
      primaryDisabledBackgroundColor: defaultAppColorPalette.gray20,
      primaryDisabledBorderColor: defaultAppColorPalette.gray20,
      primaryFocusDisabledBackgroundColor: defaultAppColorPalette.gray20,
      defaultTextLabelColorForPrimaryBackground: defaultAppColorPalette.white,
      textDisabledColorForPrimaryBackground: defaultAppColorPalette.gray70,
      primaryTextColor: defaultAppColorPalette.primary,
      primaryTextHoverColor: defaultAppColorPalette.shade10,
      primaryTextActiveColor: defaultAppColorPalette.shade20,
      controlBackgroundColorForPrimaryBackground: defaultAppColorPalette.white,
      controlHoverBackgroundColorForPrimaryBackground: defaultAppColorPalette.shade10,
      controlPressedBackgroundColorForPrimaryBackground: defaultAppColorPalette.shade30,
    };
  }
);

const primaryControlSemanticColorsWithTheme = memoizeFunction(
  (theme: ITheme): PrimaryControlSemanticColors => {
    return {
      /**
       * Common control scenarios that use App specific primary colors and its shades/tints
       */
      primaryDefaultBackgroundColor: theme.palette.themePrimary,
      primaryHoverBackgroundColor: theme.palette.themeDarkAlt,
      primarySelectedBackgroundColor: theme.palette.themeDark,
      primaryPressedBackgroundColor: theme.palette.themeDarker,
      primaryFocusBackgroundColor: theme.palette.themeDark,
      primaryFocusBorderColor: theme.palette.white,
      primaryDisabledBackgroundColor: theme.palette.neutralLighterAlt, // gray20 #f3f2f1
      primaryDisabledBorderColor: theme.palette.neutralLighterAlt,
      primaryFocusDisabledBackgroundColor: theme.palette.neutralLighterAlt,
      defaultTextLabelColorForPrimaryBackground: theme.palette.white,
      textDisabledColorForPrimaryBackground: theme.palette.neutralTertiaryAlt,
      primaryTextColor: theme.palette.themePrimary,
      primaryTextHoverColor: theme.palette.themeDarkAlt,
      primaryTextActiveColor: theme.palette.themeDark,
      controlBackgroundColorForPrimaryBackground: theme.palette.white,
      controlHoverBackgroundColorForPrimaryBackground: theme.palette.themeLighter,
      controlPressedBackgroundColorForPrimaryBackground: theme.palette.themeLight,
    };
  }
);

const darkModePrimaryControlSemanticColors = memoizeFunction(
  (theme: ITheme): PrimaryControlSemanticColors => {
    const lightTheme = AppThemeManager.getInstance().getThemeForMode(AppTheme.Light);
    return {
      ...primaryControlSemanticColorsWithTheme(theme),
      primaryTextColor: theme.palette.themeDark,
      primaryFocusBorderColor: lightTheme.palette.white,
      defaultTextLabelColorForPrimaryBackground: lightTheme.palette.white,
      controlBackgroundColorForPrimaryBackground: lightTheme.palette.white,
      controlHoverBackgroundColorForPrimaryBackground: lightTheme.palette.themeLighter,
      controlPressedBackgroundColorForPrimaryBackground: lightTheme.palette.themeLight,
    };
  }
);

const modernUxPrimaryControlSemanticColors = memoizeFunction(
  (theme: ITheme): PrimaryControlSemanticColors => {
    return {
      ...primaryControlSemanticColorsWithTheme(theme),
    };
  }
);

export const getPrimaryControlSemanticColorsWithTheme = (
  theme: ITheme,
  appColors?: AppColors
): PrimaryControlSemanticColors => {
  if (isThemingEnabled()) {
    if (isSharedUXDarkModeOn()) {
      return darkModePrimaryControlSemanticColors(theme);
    }
    if (getModernUxExperimentPreference()) {
      return modernUxPrimaryControlSemanticColors(theme);
    }
    return primaryControlSemanticColorsWithTheme(theme);
  }
  return primaryControlSemanticColors(appColors);
};
